import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Button,
  Box,
  Chip,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Cookies from "js-cookie";

const applications = [
  { name: "Basic", icon: "🔧", url: "https://basic.jected.com", comingSoon: false },
  { name: "Team", icon: "👥", url: "https://team.jected.com", comingSoon: false },
  { name: "Projects", icon: "📂", url: "#", comingSoon: true },
  { name: "Stock", icon: "📦", url: "#", comingSoon: true },
  { name: "Training", icon: "🎓", url: "#", comingSoon: true },
];

const Dashboard = () => {
  const [dismissedBanner, setDismissedBanner] = useState(
    Cookies.get("dismissedBanner") === "true"
  );
  const [recentApps, setRecentApps] = useState(
    JSON.parse(Cookies.get("recentApps") || "[]").slice(0, 2)
  );
  const [favoriteApps, setFavoriteApps] = useState(
    JSON.parse(Cookies.get("favoriteApps") || "[]")
  );
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    Cookies.set("recentApps", JSON.stringify(recentApps), { expires: 7 });
  }, [recentApps]);

  useEffect(() => {
    Cookies.set("favoriteApps", JSON.stringify(favoriteApps), { expires: 7 });
  }, [favoriteApps]);

  const handleAppClick = (app) => {
    if (!app.comingSoon) {
      setRecentApps((prev) => {
        const updated = [app.name, ...prev.filter((name) => name !== app.name)];
        return updated.slice(0, 2);
      });
  
      window.open(app.url, "_blank"); // Opens in a new tab
    }
  };
  

  const toggleFavorite = (app, event) => {
    event.stopPropagation(); // Prevent click from opening the app
    setFavoriteApps((prev) => {
      return prev.includes(app.name)
        ? prev.filter((name) => name !== app.name)
        : [app.name, ...prev];
    });
  };

  const handleDismissBanner = () => {
    setDismissedBanner(true);
    Cookies.set("dismissedBanner", "true", { expires: 7 });
  };

  const handleMenuClick = (event, app) => {
    event.stopPropagation(); // Prevent click from opening the app
    setSelectedApp(app);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedApp(null);
  };

  return (
    <Container>
      {/* Logo */}
      <Box textAlign="center" my={4} sx={{mb:5}}>
        <img src="/images/logo/Logoname.png" alt="Logo" style={{ width: 200 }} />
      </Box>

      {/* Getting Started Banner */}
      {!dismissedBanner && (
        <Paper sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body1">
            Welcome! Get started by exploring your applications.
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              href="/how-to-get-started"
              sx={{ mr: 2 }}
            >
              Learn More
            </Button>
            <IconButton onClick={handleDismissBanner}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Favorite Applications */}
      {favoriteApps.length > 0 && (
        <>
          <Typography variant="h5" textAlign="center" sx={{ my: 2 }}>
            ⭐ Favorite Applications
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {applications
              .filter((app) => favoriteApps.includes(app.name))
              .map((app) => (
                <Grid item key={app.name} xs={12} sm={6} md={4}>
                  <AppCard
                    app={app}
                    onClick={handleAppClick}
                    onFavorite={toggleFavorite}
                    onMenuClick={handleMenuClick}
                    isFavorite
                  />
                </Grid>
              ))}
          </Grid>
        </>
      )}

      {/* Recent Applications */}
      {recentApps.length > 0 && (
        <>
          <Typography variant="h5" textAlign="center" sx={{ my: 2 }}>
            🔄 Recent Applications
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {applications
              .filter((app) => recentApps.includes(app.name))
              .map((app) => (
                <Grid item key={app.name} xs={12} sm={6} md={4}>
                  <AppCard
                    app={app}
                    onClick={handleAppClick}
                    onFavorite={toggleFavorite}
                    onMenuClick={handleMenuClick}
                    isFavorite={favoriteApps.includes(app.name)}
                  />
                </Grid>
              ))}
          </Grid>
        </>
      )}

      {/* All Applications */}
      <Typography variant="h5" textAlign="center" sx={{ my: 2 }}>
        📂 All Applications
      </Typography>
      <Grid container spacing={3} justifyContent="center">
        {applications.map((app) => (
          <Grid item key={app.name} xs={12} sm={6} md={4}>
            <AppCard
              app={app}
              onClick={handleAppClick}
              onFavorite={toggleFavorite}
              onMenuClick={handleMenuClick}
              isFavorite={favoriteApps.includes(app.name)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Dropdown Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {selectedApp && (
          <>
            <MenuItem component="a" href={selectedApp.url} disabled={selectedApp.comingSoon}>
              Go to Application
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>More Info</MenuItem>
          </>
        )}
      </Menu>
    </Container>
  );
};

const AppCard = ({ app, onClick, onFavorite, onMenuClick, isFavorite }) => {
  return (
    <Card sx={{ position: "relative" }}>
      <IconButton
        onClick={(e) => onFavorite(app, e)}
        sx={{ position: "absolute", top: 5, right: 5, zIndex: 2 }}
      >
        {isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
      </IconButton>
      <CardActionArea
        onClick={() => onClick(app)}
        disabled={app.comingSoon}
      >
        <CardMedia
          component="div"
          sx={{
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2rem",
            opacity: app.comingSoon ? 0.5 : 1,
          }}
        >
          {app.icon}
        </CardMedia>
        <CardContent sx={{ textAlign: "center", position: "relative" }}>
          <Typography variant="h6">
            {app.name}{" "}
            {app.comingSoon && <Chip label="Coming Soon" color="warning" size="small" sx={{ ml: 1 }} />}
          </Typography>
          <IconButton
            onClick={(e) => onMenuClick(e, app)}
            sx={{ position: "absolute", bottom: 5, right: 5 }}
          >
            <MoreVertIcon />
          </IconButton>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default Dashboard;
